// react-query v5 호환 예시
import {useMemo} from 'react';
import {ChannelT} from '@/utils/i18n/channel';
import {
  QueryClient,
  QueryCache,
  MutationCache,
  DefaultError,
} from '@tanstack/react-query';

// (선택) Axios를 사용한다면 타입 힌트용
// import type { AxiosError } from 'axios';

type StationError = {
  code: string;
  message: string;
  errorInfo?: Record<string, unknown>;
};

/**
 * 서버 에러 페이로드 표준 형태
 */
type ServerErrorBody = {
  code?: string;
  message?: string;
  errorInfo?: Record<string, unknown>;
  black?: Record<string, unknown>;
};

/**
 * 전역 에러에서 스테이션 에러코드를 뽑아 상태를 갱신하는 핸들러
 *
 * @param error - React Query가 전달하는 에러 객체(unknown에 가까움)
 */
function makeQueryErrorHandler(
  setStationError: (e: StationError) => void,
  STATION_ERROR_CODE: string[],
) {
  return function queryErrorHandler(error: DefaultError): void {
    // AxiosError처럼 생긴 객체를 너그럽게 파싱 (duck-typing)
    // const axiosErr = error as AxiosError<ServerErrorBody>;
    const response: unknown = (error as any)?.response;
    const data: ServerErrorBody | undefined = (response as any)?.data;

    const errorCode = data?.code;
    const message = data?.message ?? ChannelT('알 수 없는 에러');
    const errorDetail = data?.errorInfo || data?.black;

    if (errorCode && STATION_ERROR_CODE.includes(errorCode)) {
      setStationError({code: errorCode, message, errorInfo: errorDetail});
    }
  };
}

/**
 * v5 기준의 QueryClient 생성 훅
 *
 * - 전역 에러는 QueryCache/MutationCache의 onError에서 처리
 * - defaultOptions에는 재시도/리패치 정책만 둠
 */
export function useAppQueryClient(
  setStationError: (e: StationError) => void,
  STATION_ERROR_CODE: string[],
) {
  // 의존성 배열에 setter와 코드 맵을 넣어, 변경 시 새 클라이언트 생성
  return useMemo(() => {
    const onError = makeQueryErrorHandler(setStationError, STATION_ERROR_CODE);

    return new QueryClient({
      queryCache: new QueryCache({onError}),
      mutationCache: new MutationCache({onError}),

      defaultOptions: {
        queries: {
          staleTime: Infinity,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          refetchInterval: false,
          retry: false,
        },
        mutations: {
          retry: false,
        },
      },
    });
  }, [setStationError, STATION_ERROR_CODE]);
}
